<script setup lang="ts">
import { computed, ref } from 'vue';
import { escapeRegExpSpecialCharacters } from '@/utils/regexp';
import { escapeHTML, sanitize } from '@/utils/sanitize';
import { sliceByCodePoints } from '@/utils/string';

interface Props {
  highlightKeywords: string[];
  text: string;
  lineClamp: number;
}

const props = defineProps<Props>();

const contentRef = ref<HTMLElement | null>(null);

// 空文字・空白のみのキーワードは除外する。空文字が混ざると1文字ごとに空の span を挿入してしまい性能に影響する
const validKeywords = computed(() =>
  props.highlightKeywords.filter(keyword => keyword.trim().length > 0),
);

const keywordPattern = computed(() =>
  validKeywords.value
    .map(keyword => escapeRegExpSpecialCharacters(keyword))
    .join('|'),
);

const firstHitKeywordPositionIndex = computed(() => {
  if (validKeywords.value.length === 0) return -1;
  return new RegExp(keywordPattern.value, 'i').exec(props.text)?.index ?? -1;
});

const partsContentHtml = computed(() => {
  const chunkText = props.text;
  // 最大許容文字数をコンポーネントの幅から計算
  const clientWidth = contentRef.value?.clientWidth ?? 0;
  const maxDisplayContentWordSize =
    clientWidth > 0 ? Math.floor((clientWidth * 3) / 15) : 80;
  const maxDisplayLength = maxDisplayContentWordSize * props.lineClamp;

  if (firstHitKeywordPositionIndex.value === -1) {
    return sanitize(sliceByCodePoints(chunkText, 0, maxDisplayLength));
  }

  // 最初にヒットしたキーワードの位置以降のテキストを取得
  const slicedContentFromHitKeyword = chunkText.slice(
    firstHitKeywordPositionIndex.value,
  );
  // 表示文章が文末かどうかを判定
  const isEndOfSentence =
    [...slicedContentFromHitKeyword].length <= maxDisplayContentWordSize &&
    firstHitKeywordPositionIndex.value !== 0;
  // ハイライトのためのキーワードの位置を計算
  const startPosition = isEndOfSentence
    ? [...chunkText].length - maxDisplayContentWordSize
    : firstHitKeywordPositionIndex.value;
  // 表示するテキスト
  const slicedContent = isEndOfSentence
    ? sliceByCodePoints(chunkText, -maxDisplayContentWordSize)
    : sliceByCodePoints(slicedContentFromHitKeyword, 0, maxDisplayLength);

  const beforeEllipsis = startPosition > 0 ? '…' : '';
  return sanitize(beforeEllipsis + replaceHighlight(slicedContent), {
    allowedTags: ['span'],
    allowedAttributes: {
      span: ['class'],
    },
  });
});

const replaceHighlight = (text: string) =>
  text.replace(
    new RegExp(keywordPattern.value, 'gi'),
    match => `<span class="highlight">${escapeHTML(match)}</span>`,
  );
</script>

<template>
  <div class="quote">
    <!-- eslint-disable vue/no-v-html -->
    <div
      class="quote-content c-text-regular--m"
      ref="contentRef"
      v-html="partsContentHtml"
    ></div>
    <!-- eslint-enable vue/no-v-html -->
  </div>
</template>

<style lang="scss" scoped>
.quote {
  overflow: hidden;
  display: -webkit-box;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: v-bind('lineClamp');
  vertical-align: bottom;
  word-break: break-all;

  font-weight: 400;
  color: $color-gray800;

  .quote-content {
    :deep(.highlight) {
      background-color: $color-gray400;
      color: $color-gray1000;
    }
  }
}
</style>
